import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiRequest } from '../services/queryClient';
import { api } from '../services/api';
import { showApiError } from '@/lib/toast-utils';
import { useUser } from '@/contexts/UserContext';
import type { Photo, Event } from '../types/schema';

export interface Favorite {
  id: string;
  itemId: string;
  itemType: 'photo' | 'event';
  createdAt: string;
  itemDetails: Photo | Event | null;
}

export interface UseFavoritesReturn {
  favorites: Favorite[];
  isLoading: boolean;
  error: string | null;
  totalFavorites: number;
  addToFavorites: (itemId: string, itemType: 'photo' | 'event') => Promise<void>;
  removeFromFavorites: (itemId: string, itemType: 'photo' | 'event') => Promise<void>;
  clearAllFavorites: () => Promise<void>;
  isFavorite: (itemId: string, itemType: 'photo' | 'event') => boolean;
  checkIsFavorite: (itemId: string, itemType: 'photo' | 'event') => Promise<boolean>;
  refreshFavorites: () => Promise<void>;
}

export function useFavorites(): UseFavoritesReturn {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isLoggedIn } = useUser();

  // Buscar favoritos do usuário
  const refreshFavorites = useCallback(async () => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {

      setFavorites([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);



      const response = await api.get('/favorites');



      // Garantir que sempre seja um array
      const favoritesData = response?.data || response || [];
      const favoritesArray = Array.isArray(favoritesData) ? favoritesData : [];



      setFavorites(favoritesArray);

    } catch (err: any) {
      console.error('Erro ao buscar favoritos:', err);

      // Se for erro 401, não mostrar erro para o usuário, apenas limpar favoritos
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {

        setFavorites([]);
        setError(null);
      } else {
        setError(err.response?.data?.error || 'Erro ao carregar favoritos');
        showApiError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  // Adicionar item aos favoritos
  const addToFavorites = useCallback(async (itemId: string, itemType: 'photo' | 'event') => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {

      return;
    }

    try {
      setError(null);

      const response = await api.post('/favorites', { itemId, itemType });
      // Atualizar lista local
      await refreshFavorites();
    } catch (err: any) {
      console.error('Erro ao adicionar favorito:', err);

      // Se for erro 401, não mostrar erro para o usuário
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setError(null);
      } else {
        setError(err.response?.data?.error || 'Erro ao adicionar aos favoritos');
        showApiError(err);
      }
      throw err;
    }
  }, [refreshFavorites, isLoggedIn]);

  // Remover item dos favoritos
  const removeFromFavorites = useCallback(async (itemId: string, itemType: 'photo' | 'event') => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {
      return;
    }

    try {
      setError(null);
      const response = await api.delete(`/favorites/${itemId}/${itemType}`);
      // Atualizar lista local
      await refreshFavorites();
    } catch (err: any) {
      console.error('Erro ao remover favorito:', err);

      // Se for erro 401, não mostrar erro para o usuário
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setError(null);
      } else {
        setError(err.response?.data?.error || 'Erro ao remover dos favoritos');
        showApiError(err);
      }
      throw err;
    }
  }, [refreshFavorites, isLoggedIn]);

  // Verificar se item está nos favoritos (busca local)
  const isFavorite = useCallback((itemId: string, itemType: 'photo' | 'event') => {
    if (!isLoggedIn) return false;
    return favorites.some(fav => fav.itemId === itemId && fav.itemType === itemType);
  }, [favorites, isLoggedIn]);

  // Verificar se item está nos favoritos (busca na API)
  const checkIsFavorite = useCallback(async (itemId: string, itemType: 'photo' | 'event') => {
    // Não fazer requisição se o usuário não estiver logado
    if (!isLoggedIn) {
      return false;
    }

    try {
      const response = await api.get(`/favorites/check/${itemId}/${itemType}`);

      return response.data?.isFavorite || false;
    } catch (err: any) {
      console.error('Erro ao verificar favorito:', err);

      // Se for erro 401, retornar false silenciosamente
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {

        return false;
      }

      return false;
    }
  }, [isLoggedIn]);

  // Carregar favoritos na inicialização apenas se logado
  useEffect(() => {
    if (isLoggedIn) {
      refreshFavorites();
    } else {
      setFavorites([]);
    }
  }, [refreshFavorites, isLoggedIn]);

  const clearAllFavorites = useCallback(async () => {
    if (!isLoggedIn) {
      console.warn('Usuário não está logado');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.delete('/favorites/clear');

      if (response.data?.success) {
        // Atualizar lista local imediatamente
        setFavorites([]);
        // Também fazer refresh para garantir sincronização
        await refreshFavorites();
      } else {
        throw new Error(response.data?.message || 'Erro ao limpar favoritos');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao limpar favoritos';

      // Se for erro 401, não mostrar erro para o usuário
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setError(null);
      } else {
        setError(errorMessage);
        showApiError(errorMessage);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, refreshFavorites]);

  // Calcular total de favoritos
  const totalFavorites = favorites.length;

  return {
    favorites,
    isLoading,
    error,
    totalFavorites,
    addToFavorites,
    removeFromFavorites,
    clearAllFavorites,
    isFavorite,
    checkIsFavorite,
    refreshFavorites,
  };
}